import {ColorSchemeType} from 'platform/components';
import {match} from 'ts-pattern';

import {isEmpty} from 'ramda';

import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

export interface SupplierStateFlagProps {
  colorScheme: ColorSchemeType;
  label: string;
}

/**
 * Maps a raw supplier state code (returned by the backend for integrated supplier orders and their
 * line items) to a localized, color-coded flag. Shared by the order and item detail windows so both
 * render the supplier status identically. Returns `null` when there is no state to display; unknown
 * codes fall back to a neutral flag showing the raw code so a new backend value is never swallowed.
 */
export const getSupplierStateFlagProps = (
  state: string | Nullish
): SupplierStateFlagProps | null => {
  if (typeof state !== 'string' || isEmpty(state)) {
    return null;
  }

  return match(state)
    .with('ACCEPTED', () => ({
      colorScheme: 'green' as const,
      label: i18n.t('entity.warehouse.labels.supplierState.accepted'),
    }))
    .with('PARTIAL', () => ({
      colorScheme: 'orange' as const,
      label: i18n.t('entity.warehouse.labels.supplierState.partial'),
    }))
    .with('REJECTED', () => ({
      colorScheme: 'red' as const,
      label: i18n.t('entity.warehouse.labels.supplierState.rejected'),
    }))
    .otherwise((rawState) => ({colorScheme: 'neutral' as const, label: rawState}));
};
